import { WEEKDAY_LONG, WEEKDAY_PLURAL, WEEKDAY_SHORT, type DraftItem, type TimetableDraft } from "@teamna/shared";
import { useRef } from "react";
import { AddLayer } from "../calendar/AddLayer.tsx";
import { CalendarBoard, type CalendarBlock, type CalendarChange, type CalendarColumn } from "../calendar/CalendarBoard.tsx";
import { NO_ONE } from "./CoachStrip.tsx";
import { bookedNextOn, shortTimeLabel, timeLabel, weekColumns, type BookedIndex } from "./model.ts";

/**
 * The week of the timetable editor, on the calendar the Schedule page shares
 * (`../calendar/CalendarBoard.tsx`): one column per weekday (Saturday first), the weekly times as
 * blocks, and each person's free hours for private sessions as a thin striped strip.
 *
 * - With a level picked (`brush`), a click on a day adds that level there. The day is a button, so
 *   a keyboard Enter adds it at 4:00 PM. The level stays picked, so the owner adds time after time.
 * - A block opens in a card next to the week, also while a level is picked. While the card is open,
 *   a click on an empty part of the week closes it (`onBackground`) and adds nothing. Dragged up or
 *   down a block moves the time on all its days; dragged sideways it moves only that day. Its lower
 *   edge changes the length. 15 minute steps (private hours: the length of one private session).
 * - The open weekly time, or else the one just added (`hint`), shows dashed on the other open days.
 *   A click on one adds that day.
 * - An empty month shows the weekly times of the month before as dashed blocks (`placeholders`):
 *   what a copy brings in. They take no click.
 */

/** A level on the board. `staffUserIds`: its coaches, in the owner's order. */
export type BoardGroup = { id: string; name: string; color: string; kind: "group" | "private"; durationMin: number; staffUserIds: string[] };

export function WeekBoard({
  draft,
  removed,
  placeholders = [],
  placeholderWords = "",
  groups,
  personName,
  booked,
  daysOff,
  brush,
  selected,
  hint,
  flagged,
  nowMinute,
  hourHeight = 72,
  usualHours,
  onAdd,
  onSelect,
  onDragStart,
  onDragMove,
  onDragEnd,
  onDayOn,
  onDayOff,
  onBackground,
  highlight = null,
}: {
  draft: TimetableDraft;
  /** Weekly times parents see that the draft takes off: drawn faded, a click opens them to put back. */
  removed: DraftItem[];
  /** The month before, on an empty month: dashed, no clicks. */
  placeholders?: DraftItem[];
  /** The end of a placeholder's accessible name: "in November". */
  placeholderWords?: string;
  groups: Map<string, BoardGroup>;
  personName: (userId: string) => string;
  booked: BookedIndex;
  daysOff: readonly number[];
  brush: { groupId: string; staffUserId: string | null } | null;
  selected: string | null;
  /** The weekly time whose other days show dashed: the open one, or the one just added. */
  hint: DraftItem | null;
  /** Items with a problem: drawn with a red edge. */
  flagged: Set<string>;
  hourHeight?: number;
  /** The hours the club usually works: the board opens on them. It always has the whole day. */
  usualHours: { start: number; end: number };
  onAdd: (weekday: number, minute: number) => void;
  onSelect: (key: string) => void;
  onDragStart: (key: string, label: string) => void;
  onDragMove: (item: DraftItem) => void;
  onDragEnd: (item: DraftItem, origin: DraftItem, mode: "move" | "resize") => void;
  /** Adds a day to the `hint` time. */
  onDayOn: (weekday: number) => void;
  /** A click on an empty part of the week while a block is open: closes the card. */
  onBackground: () => void;
  /** Takes a day off the selected time; its only day takes the whole time off. */
  onDayOff: (weekday: number) => void;
  /** A person picked on the coach row (`NO_ONE` for blocks with no coach): other blocks fade. */
  highlight?: string | null;
  /** Minutes after midnight, now, in the business time zone: the board opens there, not at the top. */
  nowMinute?: number;
}) {
  // The weekly time as it was when the drag started: every step of the drag starts from it.
  const origin = useRef<DraftItem | null>(null);
  const brushGroup = brush ? groups.get(brush.groupId) : undefined;
  const brushLength = brushGroup ? (brushGroup.kind === "private" ? 120 : brushGroup.durationMin) : 60;

  const columns: CalendarColumn[] = weekColumns().map((weekday) => {
    const off = daysOff.includes(weekday);
    const classes = draft.items.filter((item) => !item.free && item.days.includes(weekday)).length;
    const free = draft.items.filter((item) => item.free && item.days.includes(weekday)).length;
    const own = draft.items.filter((item) => item.days.includes(weekday)).length;
    return {
      key: String(weekday),
      title: off ? WEEKDAY_SHORT[weekday]! : WEEKDAY_LONG[weekday]!,
      subtitle: off ? "Day off" : `${classes} ${classes === 1 ? "session" : "sessions"}${free ? `, ${free} private` : ""}`,
      label: off ? `${WEEKDAY_LONG[weekday]}, day off` : `${WEEKDAY_LONG[weekday]}, ${own} ${own === 1 ? "weekly time" : "weekly times"}`,
      off,
    };
  });

  const shadows: CalendarBlock[] = weekColumns().flatMap((weekday) =>
    daysOff.includes(weekday)
      ? []
      : placeholders
          .filter((item) => item.days.includes(weekday))
          .map((item) => {
            const group = groups.get(item.groupId);
            const person = item.staffUserId ? personName(item.staffUserId) : null;
            const name = item.free ? `${person ?? "No one yet"}, private` : (group?.name ?? "Level");
            return {
              key: `placeholder|${item.key}|${weekday}`,
              id: `placeholder|${item.key}`,
              column: String(weekday),
              start: item.start,
              end: item.end,
              color: group?.color ?? "#5B5F73",
              label: `${name} at ${timeLabel(item.start)} on ${WEEKDAY_PLURAL[weekday]} ${placeholderWords}`.trim(),
              time: item.free ? `${timeLabel(item.start)} to ${timeLabel(item.end)}` : shortTimeLabel(item.start),
              title: item.free ? (person ?? "No one yet") : name,
              detail: item.free ? null : (person ?? "No one yet"),
              free: item.free,
              placeholder: true,
            };
          }),
  );

  const blocks: CalendarBlock[] = weekColumns().flatMap((weekday) => {
    if (daysOff.includes(weekday)) return [];
    const own = draft.items.filter((item) => item.days.includes(weekday));
    const ghosts = removed.filter((item) => item.days.includes(weekday));
    return [...own, ...ghosts].map((item) => {
      const group = groups.get(item.groupId);
      const isGhost = !own.includes(item);
      const count = bookedNextOn(item, weekday, booked);
      const person = item.staffUserId ? personName(item.staffUserId) : null;
      // The session's own name when the owner gave one ("Core awake"), else the level.
      const name = item.free ? `${person ?? "No one yet"}, private` : (item.name ?? group?.name ?? "Level");
      const counted =
        isGhost || item.ids[String(weekday)] === undefined ? "" : item.capacity === null ? `, ${count} booked` : `, ${count}/${item.capacity}`;
      return {
        key: `${item.key}|${weekday}`,
        id: item.key,
        column: String(weekday),
        start: item.start,
        end: item.end,
        color: group?.color ?? "#5B5F73",
        label: `${item.free ? `${person ?? "No one yet"} free for private sessions ${timeLabel(item.start)} to ${timeLabel(item.end)}` : `${name} at ${timeLabel(item.start)}`} on ${WEEKDAY_PLURAL[weekday]}${isGhost ? ", comes off" : ""}`,
        time: item.free ? `${timeLabel(item.start)} to ${timeLabel(item.end)}` : shortTimeLabel(item.start),
        title: item.free ? (person ?? "No one yet") : name,
        detail: item.free ? (count ? `${count} booked` : null) : `${person ?? "No one yet"}${counted}`,
        free: item.free,
        struck: isGhost,
        selected: selected === item.key,
        flagged: flagged.has(item.key),
        faded: highlight !== null && (item.staffUserId ?? NO_ONE) !== highlight,
        movable: !isGhost,
        resizable: !isGhost,
        step: item.free ? (group?.durationMin ?? 30) : 15,
      };
    });
  });

  /** The weekly time a drag gives: the new time on all its days, or the one day moved. */
  function moved(block: CalendarBlock, change: CalendarChange, mode: "move" | "resize"): DraftItem | null {
    const item = origin.current;
    if (!item) return null;
    if (mode === "resize") return { ...item, end: change.end };
    const from = Number(block.column);
    const to = Number(change.column);
    return { ...item, start: change.start, end: change.end, days: to === from ? item.days : item.days.map((day) => (day === from ? to : day)) };
  }

  return (
    <CalendarBoard
      columns={columns}
      blocks={[...shadows, ...blocks]}
      usualHours={usualHours}
      hourHeight={hourHeight}
      nowMinute={nowMinute}
      // Not while a level is picked to add weekly times.
      hoverPreview={!brush}
      onSelect={onSelect}
      // A weekly time cannot take a day it had when the drag started.
      canDrop={(_block, column) => !(origin.current?.days.includes(Number(column)) ?? true)}
      onDragStart={(block, mode) => {
        const item = draft.items.find((row) => row.key === block.id) ?? null;
        origin.current = item;
        if (!item) return;
        const group = groups.get(item.groupId);
        onDragStart(item.key, mode === "resize" ? "the length change" : group?.kind === "private" ? "the move of the free hours" : "the move");
      }}
      onDragMove={(block, change, mode) => {
        const next = moved(block, change, mode);
        if (next) onDragMove(next);
      }}
      onDragEnd={(block, change, mode) => {
        const next = moved(block, change, mode);
        if (next && origin.current) onDragEnd(next, origin.current, mode);
        origin.current = null;
      }}
      renderColumn={(column, context) => {
        const { perMinute, inset, range } = context;
        const weekday = Number(column.key);
        return (
          <>
            {selected ? (
              <button type="button" aria-label="Close the open time" tabIndex={-1} className="absolute inset-0 z-0 cursor-default" onClick={onBackground} />
            ) : brush && brushGroup ? (
              <AddLayer
                label={`Add ${brushGroup.kind === "private" ? `free hours for ${brush.staffUserId ? personName(brush.staffUserId) : "a person"}` : brushGroup.name} on ${WEEKDAY_LONG[weekday]}`}
                length={brushLength}
                name={brushGroup.kind === "private" ? (brush.staffUserId ? personName(brush.staffUserId) : "") : brushGroup.name}
                context={context}
                onAdd={(minute) => onAdd(weekday, minute)}
              />
            ) : null}

            {hint && !hint.days.includes(weekday) ? (
              <button
                type="button"
                onClick={() => onDayOn(weekday)}
                aria-label={`Add ${WEEKDAY_LONG[weekday]} at ${timeLabel(hint.start)}`}
                className="absolute z-[1] grid content-start rounded-lg border-[1.5px] border-dashed border-line-strong px-1.5 py-0.5 text-left text-[11px] font-semibold text-ink-3 tabular-nums hover:border-accent hover:bg-accent-surface/70 hover:text-accent-strong focus-visible:outline-3 focus-visible:outline-accent-soft"
                style={{ top: (hint.start - range.start) * perMinute + 1, height: Math.max(18, (hint.end - hint.start) * perMinute - 2), left: inset + 3, right: 3 }}
              >
                + {WEEKDAY_SHORT[weekday]} {timeLabel(hint.start)}
              </button>
            ) : null}
          </>
        );
      }}
      renderBlockExtra={(block, { top, left }) => {
        if (!block.selected || block.struck || block.free) return null;
        const item = draft.items.find((row) => row.key === block.id);
        if (!item) return null;
        const weekday = Number(block.column);
        const words = item.days.length > 1 ? `Take ${WEEKDAY_LONG[weekday]} off` : `Take ${block.title} at ${timeLabel(item.start)} off`;
        return (
          <button
            type="button"
            onClick={() => onDayOff(weekday)}
            aria-label={words}
            title={words}
            className="absolute z-[3] grid size-[18px] place-items-center rounded-full border border-line-strong bg-paper text-[10px] text-ink-2 hover:text-cancelled"
            style={{ top: top + 2, left }}
          >
            ✕
          </button>
        );
      }}
    />
  );
}
